import { v4 as uuidv4 } from 'uuid';

import {
  AgentSettingsInput,
  CheckStageSettingsInput,
  CustomOptions,
  CustomPromptCheckSettings,
  ExternalCheckSettingsInput,
  ExternalMaskingDetectorSettingsInput,
  GuardrailsSettingsInput,
  GuardrailsTraceMaskingMode,
  MaskingSettingsInput,
  RegexCheckSettings,
  RegexMaskingDetectorSettings,
} from '../../../../../gamma-api';

import {
  DEFAULT_CUSTOM_OPTIONS,
  DEFAULT_CUSTOM_PROMPT_CHECK_SETTINGS,
  DEFAULT_EXTERNAL_CHECK_SETTINGS,
  DEFAULT_EXTERNAL_DETECTOR_SETTINGS,
  DEFAULT_MASKING_SETTINGS,
  DEFAULT_REGEX_CHECK_SETTINGS,
  DEFAULT_REGEX_DETECTOR_SETTINGS,
} from './defaults';
import {
  GuardrailCheckSettings,
  GuardrailDetectorSettings,
  ICheckStep,
  IMaskingDetectorStep,
  CheckTypeValue,
  MaskingDetectorTypeValue,
} from './types';

export const normalizeCustomOptions = (customOptions?: CustomOptions): CustomOptions => ({
  ...DEFAULT_CUSTOM_OPTIONS,
  ...(customOptions ?? {}),
});

export const createGuardrailComponentId = (): string => uuidv4();

export const normalizeCustomPromptCheckSettings = (
  settings?: Partial<GuardrailCheckSettings>
): CustomPromptCheckSettings & { type: 'CustomPromptCheck'; name?: string } => ({
  ...DEFAULT_CUSTOM_PROMPT_CHECK_SETTINGS,
  ...(settings ?? {}),
  check_id: settings?.check_id || createGuardrailComponentId(),
  type: CheckTypeValue.CustomPromptCheck,
  llm: settings?.llm ?? DEFAULT_CUSTOM_PROMPT_CHECK_SETTINGS.llm,
  use_final_answer_tool: settings?.use_final_answer_tool ?? DEFAULT_CUSTOM_PROMPT_CHECK_SETTINGS.use_final_answer_tool,
  use_original_text_for_check:
    settings?.use_original_text_for_check ?? DEFAULT_CUSTOM_PROMPT_CHECK_SETTINGS.use_original_text_for_check,
});

export const normalizeExternalCheckSettings = (
  settings?: Partial<GuardrailCheckSettings>
): ExternalCheckSettingsInput & { type: 'ExternalCheck'; name?: string } => ({
  ...DEFAULT_EXTERNAL_CHECK_SETTINGS,
  ...(settings ?? {}),
  check_id: settings?.check_id || createGuardrailComponentId(),
  type: CheckTypeValue.ExternalCheck,
  request_headers: settings?.request_headers ?? DEFAULT_EXTERNAL_CHECK_SETTINGS.request_headers,
  custom_options: normalizeCustomOptions(settings?.custom_options),
  use_original_text_for_check:
    settings?.use_original_text_for_check ?? DEFAULT_EXTERNAL_CHECK_SETTINGS.use_original_text_for_check,
});

export const normalizeRegexCheckSettings = (
  settings?: Partial<GuardrailCheckSettings>
): RegexCheckSettings & { type: 'RegexCheck'; name?: string } => {
  const settingsWithoutReason = { ...(settings ?? {}) } as Partial<GuardrailCheckSettings> & {
    reason?: string;
  };
  delete settingsWithoutReason.reason;

  return {
    ...DEFAULT_REGEX_CHECK_SETTINGS,
    ...settingsWithoutReason,
    check_id: settings?.check_id || createGuardrailComponentId(),
    type: CheckTypeValue.RegexCheck,
    patterns: settings?.patterns ?? DEFAULT_REGEX_CHECK_SETTINGS.patterns,
    flags: settings?.flags ?? DEFAULT_REGEX_CHECK_SETTINGS.flags,
    use_original_text_for_check:
      settings?.use_original_text_for_check ?? DEFAULT_REGEX_CHECK_SETTINGS.use_original_text_for_check,
  };
};

export const normalizeCheckSettings = (check: GuardrailCheckSettings): GuardrailCheckSettings => {
  if (check.type === CheckTypeValue.ExternalCheck) {
    return normalizeExternalCheckSettings(check) as GuardrailCheckSettings;
  }

  if (check.type === CheckTypeValue.RegexCheck) {
    return normalizeRegexCheckSettings(check) as GuardrailCheckSettings;
  }

  return normalizeCustomPromptCheckSettings(check) as GuardrailCheckSettings;
};

export const normalizeCheckForStage = (check: GuardrailCheckSettings, order: number): GuardrailCheckSettings => ({
  ...normalizeCheckSettings(check),
  order,
});

export const sortCheckSettings = (checks?: GuardrailCheckSettings[]): GuardrailCheckSettings[] =>
  [...(checks ?? [])].sort((left, right) => (left.order ?? 0) - (right.order ?? 0));

export const normalizeCheckStageSettings = (stage?: CheckStageSettingsInput): CheckStageSettingsInput => ({
  checks: sortCheckSettings((stage?.checks ?? []) as GuardrailCheckSettings[]).map((check) =>
    normalizeCheckSettings(check)
  ),
});

export const normalizeRegexDetectorSettings = (
  settings?: Partial<RegexMaskingDetectorSettings>
): RegexMaskingDetectorSettings & { type: typeof MaskingDetectorTypeValue.Regex } => ({
  ...DEFAULT_REGEX_DETECTOR_SETTINGS,
  ...(settings ?? {}),
  detector_id: settings?.detector_id || createGuardrailComponentId(),
  type: MaskingDetectorTypeValue.Regex,
  patterns: settings?.patterns ?? DEFAULT_REGEX_DETECTOR_SETTINGS.patterns,
  flags: settings?.flags ?? DEFAULT_REGEX_DETECTOR_SETTINGS.flags,
});

export const normalizeExternalDetectorSettings = (
  settings?: Partial<ExternalMaskingDetectorSettingsInput>
): ExternalMaskingDetectorSettingsInput & { type: typeof MaskingDetectorTypeValue.External } => ({
  ...DEFAULT_EXTERNAL_DETECTOR_SETTINGS,
  ...(settings ?? {}),
  detector_id: settings?.detector_id || createGuardrailComponentId(),
  type: MaskingDetectorTypeValue.External,
  request_headers: settings?.request_headers ?? DEFAULT_EXTERNAL_DETECTOR_SETTINGS.request_headers,
  custom_options: normalizeCustomOptions(settings?.custom_options),
});

export const normalizeDetectorSettings = (detector: GuardrailDetectorSettings): GuardrailDetectorSettings => {
  if (detector.type === MaskingDetectorTypeValue.Regex) {
    return normalizeRegexDetectorSettings(detector) as GuardrailDetectorSettings;
  }

  if (detector.type === MaskingDetectorTypeValue.External) {
    return normalizeExternalDetectorSettings(detector) as GuardrailDetectorSettings;
  }

  return normalizeRegexDetectorSettings(detector as Partial<RegexMaskingDetectorSettings>) as GuardrailDetectorSettings;
};

export const normalizeDetectorForOrder = (
  detector: GuardrailDetectorSettings,
  order: number
): GuardrailDetectorSettings => ({
  ...normalizeDetectorSettings(detector),
  order,
});

export const sortDetectorSettings = (detectors?: GuardrailDetectorSettings[]): GuardrailDetectorSettings[] =>
  [...(detectors ?? [])].sort((left, right) => (left.order ?? 0) - (right.order ?? 0));

export const normalizeMaskingSettings = (masking?: GuardrailsSettingsInput['masking']): MaskingSettingsInput => ({
  ...DEFAULT_MASKING_SETTINGS,
  ...(masking ?? {}),
  builtin_entities: masking?.builtin_entities ?? DEFAULT_MASKING_SETTINGS.builtin_entities,
  detectors: sortDetectorSettings((masking?.detectors ?? []) as GuardrailDetectorSettings[]).map(
    normalizeDetectorSettings
  ),
});

export const normalizeGuardrailsSettings = (settings?: GuardrailsSettingsInput): GuardrailsSettingsInput => {
  return {
    trace_masking: settings?.trace_masking ?? GuardrailsTraceMaskingMode.Enabled,
    masking: normalizeMaskingSettings(settings?.masking),
    input: normalizeCheckStageSettings(settings?.input),
    output: normalizeCheckStageSettings(settings?.output),
  };
};

export const buildAgentSettingsWithGuardrails = (
  agentSettings: AgentSettingsInput,
  guardrails: GuardrailsSettingsInput
): AgentSettingsInput => ({
  ...agentSettings,
  guardrails,
});

export const createCheckStep = (key: string, check: GuardrailCheckSettings): ICheckStep => ({
  key,
  check: normalizeCheckSettings(check),
});

export const createEmptyCheckStep = (key: string, type: CheckTypeValue): ICheckStep => {
  if (type === CheckTypeValue.ExternalCheck) {
    return {
      key,
      check: normalizeExternalCheckSettings(undefined) as GuardrailCheckSettings,
    };
  }

  if (type === CheckTypeValue.RegexCheck) {
    return {
      key,
      check: normalizeRegexCheckSettings(undefined) as GuardrailCheckSettings,
    };
  }

  return {
    key,
    check: normalizeCustomPromptCheckSettings(undefined) as GuardrailCheckSettings,
  };
};

export const createDetectorStep = (key: string, detector: GuardrailDetectorSettings): IMaskingDetectorStep => ({
  key,
  detector: normalizeDetectorSettings(detector),
});

export const createEmptyDetectorStep = (key: string, type: MaskingDetectorTypeValue): IMaskingDetectorStep => {
  if (type === MaskingDetectorTypeValue.External) {
    return {
      key,
      detector: normalizeExternalDetectorSettings() as GuardrailDetectorSettings,
    };
  }

  if (type === MaskingDetectorTypeValue.Regex) {
    return {
      key,
      detector: normalizeRegexDetectorSettings() as GuardrailDetectorSettings,
    };
  }

  return {
    key,
    detector: normalizeRegexDetectorSettings() as GuardrailDetectorSettings,
  };
};
